import type {Column as ReactTableColumn, RowData} from '@tanstack/react-table';
import {useState} from 'react';
import {useDebounce} from 'react-use';

import {
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@/components/ui/dropdown-menu';
import {Input, InputItem, InputLabel} from '@/components/ui/input';
import {cn} from '@/utils/ui';

import type {TableTextFilterMeta} from '../tableFilterTypes';
import {getTextFilterValue} from '../tableFilterTypes';

export type TextFilterMenuItemProps<TData extends RowData> = {
  column: ReactTableColumn<TData, unknown>;
  filter: TableTextFilterMeta;
  label: string;
};

export function TextFilterMenuItem<TData extends RowData>({
  column,
  filter,
  label,
}: TextFilterMenuItemProps<TData>) {
  const isActive = column.getFilterValue() !== undefined;
  const [value, setValue] = useState(
    () => getTextFilterValue(column.getFilterValue()) ?? ''
  );

  useDebounce(() => column.setFilterValue(value), 400, [value]);

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger
        className={cn(isActive && 'font-medium text-secondary-500')}
      >
        {label}
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent className="p-2">
        <InputItem>
          <InputLabel className="sr-only" htmlFor={`table-filter-${column.id}`}>
            {label}
          </InputLabel>
          <Input
            id={`table-filter-${column.id}`}
            autoFocus
            onChange={evt => setValue(evt.target.value)}
            onKeyDown={evt => {
              if (evt.key !== 'Escape') evt.stopPropagation();
            }}
            placeholder={filter.placeholder ?? label}
            value={value}
          />
        </InputItem>
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}
